import React, { useState, useMemo } from 'react';
import { SwapOutlined, SearchOutlined, ToolOutlined, ExclamationCircleOutlined, CloseOutlined, BankOutlined, FireOutlined, MonitorOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Breadcrumb, Layout, Menu, theme, message, Card, Input, Space, Button, Select, Alert } from 'antd';
import {
  getDefaultConfig,
  RainbowKitProvider,
  ConnectButton
} from '@rainbow-me/rainbowkit';
import { WagmiProvider, useAccount } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { mainnet, sepolia, bsc, arbitrum, optimism, polygon, avalanche, fantom, gnosis, base } from 'wagmi/chains';
import './App.css';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import AppRoutes from './router';
import { useScreenType } from './utils/responsive';
import MobileLayout from './components/layout/MobileLayout';
import DesktopLayout from './components/layout/DesktopLayout';
import Watermark from './components/common/Watermark';

const { Header, Content, Sider } = Layout;

const navItems: MenuProps['items'] = [
  { key: 'chain', label: '链上交互', icon: <SwapOutlined /> },
  { key: 'query', label: '批量查询', icon: <SearchOutlined /> },
  { key: 'monitor', label: '监控', icon: <MonitorOutlined /> },
  { key: 'cex', label: '交易所', icon: <BankOutlined /> },
  { key: 'gas', label: '实时Gas', icon: <FireOutlined /> },
  { key: 'tools', label: '实用工具', icon: <ToolOutlined /> },
];

const siderMenus: Record<string, MenuProps['items']> = {
  chain: [
    // 批量操作
    {
      type: 'group',
      label: '批量操作',
      children: [
        {
          key: '/chain/batch-wallet',
          label: '批量生成钱包',
        },
        {
          key: '/chain/batch-transfer',
          label: '批量转账',
          children: [
            { key: '/chain/batch-transfer-evm', label: 'EVM转账' },
            { key: '/chain/batch-transfer-sol', label: 'Sol转账' },
            { key: '/chain/batch-transfer-sui', label: 'Sui转账' },
            { key: '/chain/batch-transfer-ton', label: 'Ton转账' },
          ],
        },
        {
          key: '/chain/batch-collect',
          label: '批量归集',
          children: [
            { key: '/chain/batch-collect-evm', label: 'EVM归集' },
            { key: '/chain/batch-collect-sol', label: 'Sol归集' },
            { key: '/chain/batch-collect-sui', label: 'Sui归集' },
            { key: '/chain/batch-collect-ton', label: 'Ton归集' },
          ],
        },
        { key: '/chain/batch-mint', label: '批量交互' },
        { key: '/chain/batch-approve', label: '批量授权' },
        { key: '/chain/multi-transfer', label: '多对多转账' },
      ],
    },
    // 效率工具
    {
      type: 'group',
      label: '效率工具',
      children: [
        { key: '/chain/exchange-withdraw', label: '交易所提币' },
        { key: '/chain/utxo-split', label: 'UTXO拆分' },
        { key: '/chain/utxo-merge', label: 'UTXO合并' },
      ],
    },
    // NFT
    {
      type: 'group',
      label: 'NFT',
      children: [
        { key: '/chain/nft-freemint', label: 'Freemint' },
        { key: '/chain/nft-batch-list', label: '批量挂单' },
        { key: '/chain/nft-collect', label: 'NFT归集' },
      ],
    },
    // 新协议工具
    {
      type: 'group',
      label: '新协议工具',
      children: [
        { key: '/chain/brc20', label: 'BRC20' },
        { key: '/chain/runens', label: 'Runes' },
      ],
    },
  ],
  query: [
    // 钱包管理
    {
      type: 'group',
      label: '钱包管理',
      children: [
        { key: '/query/wallet-evm', label: 'EVM钱包' },
        { key: '/query/wallet-btc', label: 'BTC钱包' },
      ],
    },
    // 批量操作
    {
      type: 'group',
      label: '批量操作',
      children: [
        {
          key: '/query/batch-balance',
          label: '批量查询余额',
          children: [
            { key: '/query/batch-balance-evm', label: 'EVM余额' },
            { key: '/query/batch-balance-btc', label: 'BTC余额' },
            { key: '/query/batch-balance-sol', label: 'SOL余额' },
            { key: '/query/batch-balance-sui', label: 'SUI余额' },
            { key: '/query/batch-balance-ton', label: 'TON余额' },
          ],
        },
        { key: '/query/batch-approve-query', label: '批量查询授权' },
        { key: '/query/batch-nft-query', label: '批量查询NFT' },
      ],
    },
    // 空投白名单查询
    {
      type: 'group',
      label: '空投白名单查询',
      children: [
        { key: '/query/airdrop-whitelist', label: '空投白名单查询' },
      ],
    },
    // 币安ALPHA
    {
      type: 'group',
      label: '币安ALPHA',
      children: [
        { key: '/query/binance-alpha', label: '币安ALPHA' },
      ],
    },
  ],
  monitor: [
    {
      type: 'group',
      label: '通知系统',
      children: [
        { key: '/monitor/channels', label: '通知渠道管理' },
        { key: '/monitor/auth', label: 'Web3认证' },
      ],
    },
    {
      type: 'group',
      label: '钱包监控',
      children: [
        {
          key: '/monitor/wallet-manage',
          label: '钱包监控管理',
          children: [
            { key: '/monitor/wallet-evm', label: 'EVM钱包监控' },
            { key: '/monitor/wallet-sol', label: 'SOL钱包监控' },
            { key: '/monitor/wallet-btc', label: 'BTC钱包监控' },
          ],
        },
        { key: '/monitor/wallet-multi', label: '多钱包买卖监控' },
        { key: '/monitor/runes-price', label: '符文价格监控' },
        { key: '/monitor/runes-mint', label: '热门符文铸造监控' },
        { key: '/monitor/inscription-mint', label: '热门铭文铭刻监控' },
        { key: '/monitor/gas-burn', label: 'GAS燃烧异常监控' },
      ],
    },
  ],
  tools: [
    { key: '/tools/rpc', label: 'RPC测速' },
    { key: '/tools/timestamp', label: '时间转换' },
    { key: '/tools/address-convert', label: '地址转换' },
    { key: '/tools/unit-convert', label: '单位转换' },
    { key: '/tools/str-hex-convert', label: '字符串/十六进制转换' },
    { key: '/tools/base64', label: 'Base64' },
    { key: '/tools/aes', label: 'AES加解密' },
    { key: '/tools/keccak', label: 'Keccak256哈希' },
    { key: '/tools/binance-wallet-checker', label: '币安钱包检测' },
    { key: '/tools/fund-flow', label: '资金流动可视化' },
    { key: '/tools/fund-flow-tracker', label: '资金流动追踪' },
    { key: '/tools/batch-fund-flow-tracker', label: '批量资金流追踪（增强版）' },
    { key: '/tools/external-tools', label: '外部工具' },
  ],
  cex: [
    { key: '/cex/announcement', label: '公告' },
    { key: '/cex/funding-fee', label: '资金费' },
    { key: '/cex/transfer-fee', label: '充提币看板' },
  ],
  gas: [
    { key: '/gas/eth', label: 'Ethereum' },
  ],
};

// 预留白名单
const whitelist: string[] = [
  // '0x0000000000176704cb964c95AF7E16a81E1fcA6F',
];

const config = getDefaultConfig({
  appName: 'DDDD Tools',
  projectId: 'a11008ba004c988afb4a1f43fc240a02', // 替换为你的WalletConnect项目ID
  chains: [mainnet, sepolia, bsc, arbitrum, optimism, polygon, avalanche, fantom, gnosis, base],
  ssr: false,
});

const queryClient = new QueryClient();

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const screenType = useScreenType();
  const isMobile = screenType === 'mobile';

  // 通过路由前缀判断主导航选中项
  const selectedNav = useMemo(() => {
    if (location.pathname.startsWith('/chain')) return 'chain';
    if (location.pathname.startsWith('/query')) return 'query';
    if (location.pathname.startsWith('/monitor')) return 'monitor';
    if (location.pathname.startsWith('/cex')) return 'cex';
    if (location.pathname.startsWith('/gas')) return 'gas';
    if (location.pathname.startsWith('/tools')) return 'tools';
    return 'chain';
  }, [location.pathname]);

  // 侧边栏菜单
  const sideMenuItems = siderMenus[selectedNav] || [];

  // 侧边栏高亮
  const selectedSide = useMemo(() => {
    // 对于空投白名单相关的路由，统一高亮主入口
    if (location.pathname.startsWith('/query/airdrop-whitelist')) {
      return ['/query/airdrop-whitelist'];
    }
    
    // 直接匹配当前路径
    const exactMatch = sideMenuItems.find(item => {
      if (item && typeof item === 'object' && 'key' in item) {
        return location.pathname === (item as any).key;
      }
      // 检查子菜单
      if (item && typeof item === 'object' && 'children' in item && Array.isArray((item as any).children)) {
        return (item as any).children.find((child: any) => child.key === location.pathname);
      }
      return false;
    });
    
    return exactMatch ? [location.pathname] : [];
  }, [sideMenuItems, location.pathname]);

  const { address, isConnected } = useAccount();

  // 白名单校验（未连接钱包或不在白名单都视为无权限）
  const isWhitelisted =
    !!address && whitelist.map(a => a.toLowerCase()).includes(address.toLowerCase());

  // React.useEffect(() => {
  //   if (isConnected && !isWhitelisted) {
  //     message.error('该地址未在白名单内，无法访问网站');
  //   }
  // }, [isConnected, isWhitelisted]);

  // 根据屏幕类型条件渲染不同布局
  if (isMobile) {
    return (
      <MobileLayout
        siderMenus={siderMenus}
        selectedNav={selectedNav}
        selectedSide={selectedSide}
      >
        <AppRoutes />
      </MobileLayout>
    );
  }

  // 桌面端布局
  return (
    <DesktopLayout
      navItems={navItems}
      siderMenus={siderMenus}
      selectedNav={selectedNav}
      selectedSide={selectedSide}
    >
      <AppRoutes />
    </DesktopLayout>
  );
};

const App: React.FC = () => (
  <WagmiProvider config={config}>
    <QueryClientProvider client={queryClient}>
      <RainbowKitProvider>
        <BrowserRouter>
          <AppContent />
          <Watermark text="0xluoye" fontSize={16} gap={100} opacity={0.08} />
        </BrowserRouter>
      </RainbowKitProvider>
    </QueryClientProvider>
  </WagmiProvider>
);

export default App;
